// Templates / Quick Builds — curated, read-only presets surfaced to end
// users. End users can browse + one-click "Build now"; creating /
// editing / duplicating presets is an admin-only flow handled outside
// this page (admins manage the templates table directly for now).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { LayerDots } from "@/components/common/LayerDots";
import { api } from "@/lib/api";
import { useConfigStore } from "@/lib/store/configStore";
import { PACKS } from "@/lib/data";
import type { Template, Volume } from "@/lib/types";

const ICON: Record<string, string> = {
  "diamond-fill": "◆",
  "diamond-outline": "◇",
  "diamond-shaded": "◈",
  "diamond-tilt": "◇",
  "circle-fill": "●",
  lightning: "⚡",
  star: "★",
  sparkles: "✦",
};

export function Templates(): JSX.Element {
  const navigate = useNavigate();
  const setConfig = useConfigStore((s) => s.setConfig);
  const pickMode = useConfigStore((s) => s.pickMode);
  const [cat, setCat] = useState<string>("all");

  const { data: templates = [] } = useQuery({
    queryKey: ["templates"],
    queryFn: api.listTemplates,
  });

  const cats = useMemo(
    () => ["all", ...Array.from(new Set(templates.map((t) => t.category)))],
    [templates],
  );

  const filtered = useMemo(
    () => (cat === "all" ? templates : templates.filter((t) => t.category === cat)),
    [cat, templates],
  );

  const apply = (t: Template): void => {
    pickMode("custom");
    setConfig({
      profileId: t.packId,
      volume: (t.volume.toString().toLowerCase() as Volume["id"]) ?? "medium",
      stories: t.storyIds ?? [],
      pipeline: {
        crm: t.layers.includes("crm"),
        tdsx: t.layers.includes("tdsx"),
        dc: t.layers.includes("dc"),
        sdm: t.layers.includes("sdm"),
        apps: t.layers.includes("apps"),
        tabnext: t.layers.includes("tabnext"),
        refresh: t.layers.includes("refresh"),
      },
    });
    navigate("/configure");
  };

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="Quick Builds"
        title="Quick Builds"
        astro={{ pose: "phase2", message: "Curated by your admins" }}
        subtitle={`${templates.length} curated presets · One click to build—skip the wizard`}
        iconGlyph="⚡"
        rightSlot={
          <button
            className="btn hero-ghost lg"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => navigate("/configure")}
          >
            Build something custom →
          </button>
        }
      />

      <div style={{ margin: "20px 0 14px" }}>
        <FilterStrip
          active={cat}
          onClick={setCat}
          options={cats.map((c) => ({
            id: c,
            label: c === "all" ? "All" : c,
            count:
              c === "all"
                ? templates.length
                : templates.filter((t) => t.category === c).length,
          }))}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}
      >
        {filtered.map((t) => {
          const pack = PACKS.find((p) => p.id === t.packId);
          return (
            <div
              key={t.id}
              className="card"
              style={{
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "var(--cb-95)",
                    color: "var(--eb-50)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  {ICON[t.icon] ?? t.icon ?? "✦"}
                </div>
                <span className="chip ghost" style={{ fontSize: 10 }}>
                  {t.category}
                </span>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: "var(--ink)",
                  }}
                >
                  {t.name}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 2,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ color: "var(--ink-2)", fontWeight: 500 }}>
                    {pack?.name}
                  </span>
                  <span style={{ color: "var(--ink-3)" }}>
                    · {t.volume} · {t.stories} stories
                  </span>
                </div>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "var(--ink-2)",
                  lineHeight: 1.5,
                  margin: 0,
                  flex: 1,
                }}
              >
                {t.description}
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: 10,
                  borderTop: "1px solid var(--bd-2)",
                }}
              >
                <LayerDots layers={t.layers} />
                <div style={{ fontSize: 10, color: "var(--ink-3)" }}>
                  {(t.useCount ?? 0).toLocaleString()} runs · {t.lastUsed ?? "—"}
                </div>
              </div>
              <button
                className="btn primary sm"
                style={{ width: "100%" }}
                onClick={() => apply(t)}
              >
                Build now →
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: "48px 32px",
              borderRadius: 12,
              border: "1px dashed var(--bd)",
              background: "var(--cb-98)",
              textAlign: "center",
              color: "var(--ink-3)",
              fontSize: 13,
            }}
          >
            No quick builds in this category yet.
          </div>
        )}
      </div>
    </div>
  );
}

function FilterStrip({
  options,
  active,
  onClick,
}: {
  options: { id: string; label: string; count?: number }[];
  active: string;
  onClick: (id: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: "inline-flex",
        background: "white",
        border: "1px solid var(--bd)",
        borderRadius: 10,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const on = opt.id === active;
        return (
          <button
            key={opt.id}
            onClick={() => onClick(opt.id)}
            style={{
              padding: "7px 14px",
              borderRadius: 7,
              border: 0,
              background: on ? "var(--eb-50)" : "transparent",
              color: on ? "white" : "var(--ink-2)",
              fontSize: 12,
              fontWeight: on ? 600 : 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {opt.label}
            {opt.count !== undefined && (
              <span
                style={{
                  padding: "1px 7px",
                  borderRadius: 999,
                  fontSize: 10,
                  background: on ? "rgba(255,255,255,.2)" : "var(--cb-95)",
                  color: on ? "white" : "var(--ink-3)",
                  fontWeight: 600,
                }}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
